/**
 * Customer-history card for the Sales Inbox detail screen.
 *
 * Renders:
 *   - customer name
 *   - totals strip (€offers · €orders · counts)
 *   - two regions, each hosting a CommandsListView (CompositeView)
 *     over a CustomerCommands collection (offers / orders).
 *
 * Marionette.LayoutView. Totals come from the collections so they
 * naturally update if we ever mutate them.
 */
define('salesInboxCommandsPanelView',[
  'module',
  'app',
  'marionette',
  'underscore',
  'salesInboxCommandsListView',
  'secondScreenBridge'
], function (Module, App, Marionette, _, CommandsListView, SecondScreen) {
  'use strict';

  Module.exports = Marionette.LayoutView.extend({
    className: 'commands-card',

    template: _.template(
      '<div class="chead">' +
      '  <h3>Customer</h3>' +
      // Several sub-accounts on the company → a switcher; otherwise the plain
      // customer name.
      '  <% if (customers && customers.length > 1) { %>' +
      '    <select class="cust-switch" title="Switch customer in this company">' +
      '      <% _.each(customers, function (cu) { %>' +
      '        <option value="<%- cu.endUserId %>" <%= cu.endUserId === activeEndUserId ? "selected" : "" %>><%- cu.name %><% if (cu.email) { %> — <%- cu.email %><% } %></option>' +
      '      <% }); %>' +
      '    </select>' +
      '  <% } else if (customerName) { %>' +
      '    <span class="cust"><%= customerName %></span>' +
      '  <% } %>' +
      '  <% if (customerVip) { %><span class="vip">VIP</span><% } %>' +
      '</div>' +
      // Customer-level value wells (from the item payload). The offers / orders
      // totals live with their lists below (see .cmd-group) so each amount sits
      // in the same container as the list it summarises.
      '<div class="cust-money">' +
      '  <div class="well lifetime"><div class="lab">Lifetime</div><div class="v">€<%= Math.round(customerValue) %></div></div>' +
      '  <div class="well"><div class="lab">This email</div><div class="v">€<%= Math.round(emailValue) %></div></div>' +
      '</div>' +
      // Push this customer to the marked second-screen tab. Buttons mirror the
      // Alt+1..5 shortcuts handled by the detail view; titles surface the keys.
      // Advanced-mode only (gated by the secondScreen flag).
      '<% if (secondScreen && endUserId) { %>' +
      '  <div class="board-actions">' +
      '    <button type="button" class="btn-board" data-target="profile" ' +
      '            title="<%= _.i18n("salesInbox.secondScreen.profile.title", null, "Open customer profile on the second screen (Alt+1)") %>">' +
      '      <%= _.i18n("salesInbox.secondScreen.profile", null, "Profile") %> <span class="kbd">⌥1</span></button>' +
      '    <button type="button" class="btn-board" data-target="offersList" ' +
      '            title="<%= _.i18n("salesInbox.secondScreen.offersList.title", null, "Open the customer offers list on the second screen (Alt+2)") %>">' +
      '      <%= _.i18n("salesInbox.secondScreen.offersList", null, "Offers") %> <span class="kbd">⌥2</span></button>' +
      '    <button type="button" class="btn-board" data-target="ordersList" ' +
      '            title="<%= _.i18n("salesInbox.secondScreen.ordersList.title", null, "Open the customer orders list on the second screen (Alt+3)") %>">' +
      '      <%= _.i18n("salesInbox.secondScreen.ordersList", null, "Orders") %> <span class="kbd">⌥3</span></button>' +
      '    <button type="button" class="btn-board" data-target="offersBoard" ' +
      '            title="<%= _.i18n("salesInbox.secondScreen.offersBoard.title", null, "Open the offers board filtered to this customer on the second screen (Alt+4)") %>">' +
      '      <%= _.i18n("salesInbox.secondScreen.offersBoard", null, "Offers board") %> <span class="kbd">⌥4</span></button>' +
      '    <button type="button" class="btn-board" data-target="ordersBoard" ' +
      '            title="<%= _.i18n("salesInbox.secondScreen.ordersBoard.title", null, "Open the orders board filtered to this customer on the second screen (Alt+5)") %>">' +
      '      <%= _.i18n("salesInbox.secondScreen.ordersBoard", null, "Orders board") %> <span class="kbd">⌥5</span></button>' +
      '  </div>' +
      '<% } %>' +
      '<% if (loading) { %>' +
      '  <div class="empty">Loading…</div>' +
      '<% } %>' +
      // Each total now heads its own grouped container with the matching list,
      // giving offers and orders clear visual separation. Regions stay always
      // present, even while loading, so Marionette can bind them on the first
      // render — the views are only mounted inside onShow when data is ready.
      '<div class="cmd-group">' +
      '  <div class="cmd-group-head">' +
      '    <span class="lab">Open offers</span>' +
      '    <span class="amt">€<%= Math.round(offersTotal) %> <span class="n"><%= offersCount %></span></span>' +
      '  </div>' +
      '  <div class="offers-region"></div>' +
      '</div>' +
      '<div class="cmd-group">' +
      '  <div class="cmd-group-head">' +
      '    <span class="lab">Orders</span>' +
      '    <span class="amt">€<%= Math.round(ordersTotal) %> <span class="n"><%= ordersCount %></span></span>' +
      '  </div>' +
      '  <div class="orders-region"></div>' +
      '</div>'
    ),

    regions: {
      offersRegion: '.offers-region',
      ordersRegion: '.orders-region'
    },

    events: {
      'click .btn-board': 'onBoardClick',
      'change .cust-switch': 'onSwitchCustomer'
    },

    initialize: function (options) {
      options = options || {};
      this.customerName    = options.customerName;
      this.endUserId       = options.endUserId;
      this.customerVip     = !!options.customerVip;
      this.customerValue   = Number(options.customerValue) || 0;
      this.emailValue      = Number(options.emailValue) || 0;
      this.secondScreen    = !!options.secondScreen;
      this.loading         = !!options.loading;
      this.offersCollection = options.offersCollection;
      this.ordersCollection = options.ordersCollection;
      // Sub-account switcher: the company's customers + which one is active,
      // and the host callback that swaps the shown customer.
      this.customers       = options.customers || null;
      this.activeEndUserId = options.activeEndUserId;
      this.onSwitch        = options.onSwitchCustomer || null;
      // Host callback: load a status-change mail template into the reply
      // composer instead of opening the popup. Passed down to each row.
      this.onComposeTemplate = options.onComposeTemplate || null;
    },

    onBoardClick: function (e) {
      SecondScreen.openCustomer(e.currentTarget.getAttribute('data-target'), this.endUserId);
    },

    onSwitchCustomer: function (e) {
      if (this.onSwitch) { this.onSwitch(e.currentTarget.value); }
    },

    templateHelpers: function () {
      // Each row is an app.ns.Order — `price` is the line/command total.
      function sum(c) {
        if (!c) { return 0; }
        return c.reduce(function (acc, m) { return acc + (Number(m.get('price')) || 0); }, 0);
      }
      return {
        customerName: this.customerName,
        endUserId: this.endUserId,
        customerVip: this.customerVip,
        customers: this.customers,
        activeEndUserId: this.activeEndUserId,
        customerValue: this.customerValue,
        emailValue: this.emailValue,
        secondScreen: this.secondScreen,
        loading: this.loading,
        offersTotal: sum(this.offersCollection),
        ordersTotal: sum(this.ordersCollection),
        offersCount: this.offersCollection ? this.offersCollection.length : 0,
        ordersCount: this.ordersCollection ? this.ordersCollection.length : 0
      };
    },

    onShow: function () {
      if (this.loading) { return; }
      this.getRegion('offersRegion').show(new CommandsListView({
        collection: this.offersCollection,
        title: 'Offers',
        emptyMessage: 'No offers for this customer.',
        commandType: 'offer',
        // Advanced-mode only: lets each row open on the second screen.
        secondScreen: this.secondScreen,
        onComposeTemplate: this.onComposeTemplate
      }));
      this.getRegion('ordersRegion').show(new CommandsListView({
        collection: this.ordersCollection,
        title: 'Orders',
        emptyMessage: 'No confirmed orders.',
        commandType: 'command',
        secondScreen: this.secondScreen,
        onComposeTemplate: this.onComposeTemplate
      }));
    }
  });
});

